import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import {
  resolvePhotoPrivacySettings,
  type District,
  type DistrictPhotoPrivacySettings,
  type PhotoPrivacyTarget,
} from '@citizen-walk/shared-types';
import { useState } from 'react';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';

/**
 * 地区設定「写真のプライバシー」(docs/28 §8-1・§10-4)。
 *
 * - 自動ぼかし (対象: 人の顔 / 車のナンバープレート) と、ぼかし前の写真の保存 (保存する日数) は
 *   マスター管理者と地区管理者が変えられる。
 * - 「地区管理者がぼかし前の写真を見られる」「地区管理者にぼかし前の写真のエクスポートを許可する」は
 *   マスター管理者だけが変えられる (firestore.rules で地区管理者からの変更を拒否、D8・D13)。
 *
 * 書込先: `districts/{id}.photoPrivacy` (setDoc merge)。地区管理者は許可 2 項目を送らない
 * (送らなければ merge で現在の値が残る)。
 */

export interface PhotoPrivacyDraft {
  autoBlurEnabled: boolean;
  targets: PhotoPrivacyTarget[];
  keepOriginals: boolean;
  /** 空文字 = 無期限。 */
  retentionDays: string;
  allowDistrictAdminViewOriginal: boolean;
  allowDistrictAdminOriginalExport: boolean;
}

/** pure: District → draft (未設定は既定値)。 */
export function readPhotoPrivacyDraft(d: Pick<District, 'photoPrivacy'>): PhotoPrivacyDraft {
  const r = resolvePhotoPrivacySettings(d.photoPrivacy);
  return {
    autoBlurEnabled: r.autoBlurEnabled,
    targets: r.targets,
    keepOriginals: r.keepOriginals,
    retentionDays: r.retentionDays === null ? '' : String(r.retentionDays),
    allowDistrictAdminViewOriginal: r.allowDistrictAdminViewOriginal,
    allowDistrictAdminOriginalExport: r.allowDistrictAdminOriginalExport,
  };
}

/** 保存日数の入力の検証。空欄 = 無期限。エラーなら日本語の文言を返す。 */
export function validateRetentionDays(raw: string): { value: number | null; error: string | null } {
  const s = raw.trim();
  if (s === '') return { value: null, error: null };
  const n = Number(s);
  if (!Number.isInteger(n) || n < 1) {
    return {
      value: null,
      error: '保存する日数は 1 以上の整数で入力してください（空欄なら無期限）',
    };
  }
  return { value: n, error: null };
}

/**
 * pure: draft → 保存する内容。`isMaster=false` (地区管理者) のときは許可 2 項目を含めない。
 * 対象が 1 つも選ばれていなければエラー。
 */
export function buildPhotoPrivacyPayload(
  draft: PhotoPrivacyDraft,
  isMaster: boolean,
): { payload: DistrictPhotoPrivacySettings | null; error: string | null } {
  const days = validateRetentionDays(draft.retentionDays);
  if (days.error) return { payload: null, error: days.error };
  if (draft.autoBlurEnabled && draft.targets.length === 0) {
    return { payload: null, error: 'ぼかす対象を 1 つ以上選んでください' };
  }
  const payload: DistrictPhotoPrivacySettings = {
    autoBlur: { enabled: draft.autoBlurEnabled, targets: [...draft.targets] },
    keepOriginals: { enabled: draft.keepOriginals, retentionDays: days.value },
  };
  if (isMaster) {
    payload.allowDistrictAdminViewOriginal = draft.allowDistrictAdminViewOriginal;
    // 見られない地区ではエクスポートの許可も効かない (§5-5)。保存値もそろえる。
    payload.allowDistrictAdminOriginalExport =
      draft.allowDistrictAdminViewOriginal && draft.allowDistrictAdminOriginalExport;
  }
  return { payload, error: null };
}

const TARGET_LABELS: Record<PhotoPrivacyTarget, string> = {
  face: '人の顔',
  licensePlate: '車のナンバープレート',
};

export interface DistrictPhotoPrivacyPanelProps {
  district: District;
  uid: string;
  isMaster: boolean;
}

export function DistrictPhotoPrivacyPanel({
  district,
  uid,
  isMaster,
}: DistrictPhotoPrivacyPanelProps) {
  const { toast } = useToast();
  const [draft, setDraft] = useState<PhotoPrivacyDraft>(() => readPhotoPrivacyDraft(district));
  const [saving, setSaving] = useState(false);
  const retention = validateRetentionDays(draft.retentionDays);

  function toggleTarget(t: PhotoPrivacyTarget, on: boolean) {
    const next = on ? [...new Set([...draft.targets, t])] : draft.targets.filter((x) => x !== t);
    setDraft({ ...draft, targets: next });
  }

  async function handleSave() {
    const { payload, error } = buildPhotoPrivacyPayload(draft, isMaster);
    if (!payload) {
      toast('error', error ?? '入力内容を確認してください');
      return;
    }
    setSaving(true);
    try {
      await setDoc(
        doc(db, 'districts', district.id),
        { photoPrivacy: payload, updatedAt: serverTimestamp(), updatedBy: uid },
        { merge: true },
      );
      toast('success', '写真のプライバシーの設定を保存しました');
    } catch (e) {
      toast('error', `保存できませんでした: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card padding="lg" className="border-primary-200" data-testid="district-photo-privacy-panel">
      <h3 className="mb-1 text-base font-bold text-slate-900">🛡️ 写真のプライバシー</h3>
      <p className="mb-3 text-xs text-slate-500">
        投稿写真に写った人の顔や車のナンバープレートを、サーバーで自動的に見つけてぼかしてから公開します。
        自動のため見落とすことがあります。見落としは投稿の管理画面から手でぼかしを追加できます。
      </p>

      <div className="grid grid-cols-1 gap-4">
        <section className="grid grid-cols-1 gap-2">
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={draft.autoBlurEnabled}
              onChange={(e) => setDraft({ ...draft, autoBlurEnabled: e.target.checked })}
              disabled={saving}
              className="mt-0.5 h-4 w-4"
              data-testid="privacy-auto-blur"
            />
            <span className="text-sm">
              写真を自動でぼかす
              <span className="block text-[11px] text-slate-400">
                オフにしても、写真の位置情報（EXIF）は公開前に取り除きます。設定の変更はこれから投稿される写真に効きます。
              </span>
            </span>
          </label>
          <div className="ml-6 flex flex-wrap gap-4">
            {(Object.keys(TARGET_LABELS) as PhotoPrivacyTarget[]).map((t) => (
              <label key={t} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={draft.targets.includes(t)}
                  onChange={(e) => toggleTarget(t, e.target.checked)}
                  disabled={saving || !draft.autoBlurEnabled}
                  className="h-4 w-4"
                  data-testid={`privacy-target-${t}`}
                />
                {TARGET_LABELS[t]}
              </label>
            ))}
          </div>
        </section>

        <section className="grid grid-cols-1 gap-2">
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={draft.keepOriginals}
              onChange={(e) => setDraft({ ...draft, keepOriginals: e.target.checked })}
              disabled={saving}
              className="mt-0.5 h-4 w-4"
              data-testid="privacy-keep-originals"
            />
            <span className="text-sm">
              ぼかし前の写真を保存する
              <span className="block text-[11px] text-slate-400">
                投稿者本人は「自分の写真のダウンロード」でぼかし前の写真を受け取れます。管理者はぼかしの確認・やり直しに使います。
                オフにしても、すでに保存されている写真は自動では消えません。
              </span>
            </span>
          </label>
          <label className="ml-6 grid max-w-xs grid-cols-1 gap-1">
            <span className="text-xs text-slate-500">
              保存する日数（投稿日から。空欄なら無期限）
            </span>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              value={draft.retentionDays}
              onChange={(e) => setDraft({ ...draft, retentionDays: e.target.value })}
              disabled={saving || !draft.keepOriginals}
              className="min-h-touch rounded border border-slate-300 bg-white px-3 text-sm disabled:bg-slate-100 disabled:text-slate-400"
              data-testid="privacy-retention-days"
            />
            {retention.error ? (
              <span className="text-[11px] text-danger-700" role="alert">
                {retention.error}
              </span>
            ) : (
              <span className="text-[11px] text-slate-400">
                日数を短くすると、期間を過ぎたぼかし前の写真は次の削除の処理（1 日 1
                回）で消えます。
              </span>
            )}
          </label>
        </section>

        <section className="grid grid-cols-1 gap-2 rounded border border-slate-200 bg-slate-50 p-3">
          <p className="text-xs font-bold text-slate-700">
            地区管理者への許可（マスター管理者のみ変更できます）
          </p>
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={draft.allowDistrictAdminViewOriginal}
              onChange={(e) =>
                setDraft({ ...draft, allowDistrictAdminViewOriginal: e.target.checked })
              }
              disabled={saving || !isMaster}
              className="mt-0.5 h-4 w-4"
              data-testid="privacy-allow-view"
            />
            <span className="text-sm">
              地区管理者がぼかし前の写真を見られるようにする
              <span className="block text-[11px] text-slate-400">
                オフのとき、ぼかしに失敗した写真の確認はマスター管理者が行います。
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={
                draft.allowDistrictAdminViewOriginal && draft.allowDistrictAdminOriginalExport
              }
              onChange={(e) =>
                setDraft({ ...draft, allowDistrictAdminOriginalExport: e.target.checked })
              }
              disabled={saving || !isMaster || !draft.allowDistrictAdminViewOriginal}
              className="mt-0.5 h-4 w-4"
              data-testid="privacy-allow-export"
            />
            <span className="text-sm">地区管理者にぼかし前の写真のエクスポートを許可する</span>
          </label>
        </section>
      </div>

      <div className="mt-3 flex justify-end">
        <Button
          onClick={() => void handleSave()}
          disabled={saving || retention.error !== null}
          data-testid="privacy-save"
        >
          {saving ? '保存中…' : '保存'}
        </Button>
      </div>
    </Card>
  );
}
